import { HttpErrorResponse } from '@angular/common/http';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  APIResponse,
  Role,
  User,
  UserProject,
  UserRole,
  UserRoleUnit,
  UsersSortTypes,
  UserStatuses,
} from '@macs-frontend/features/shared';
import { Action } from '@ngrx/store';
import { FormControlState } from 'ngrx-forms';
import { HideLoader, ShowLoader } from '@macs-frontend/web-macs/decorators';
import { ResponseTeamMember } from '../../../models/response-team-member';
import { defaultItemsPerPage } from '../../../reducers/manage';

export enum UsersActionTypes {
  LoadAllSuccess = '[ManageUsers] LoadAll Success',
  LoadAll = '[ManageUsers] LoadAll',
  LoadFail = '[ManageUsers] LoadFail',
  LoadPage = '[ManageUsers] LoadPage',
  Search = '[ManageUsers] Search',
  SortBy = '[ManageUsers] SortBy',
  DeleteUser = '[ManageUsers] DeleteUser',
  RestoreUser = '[ManageUsers] RestoreUser',
  UserDeleted = '[ManageUsers] UserDeleted',
  UserRestored = '[ManageUsers] UserRestored',
  LoadRoles = '[ManageUsers] LoadRoles',
  RolesLoaded = '[ManageUsers] RolesLoaded',
  ChangePageLimit = '[ManageUsers] ChangePageLimit',
  CreateUser = '[ManageUsers] CreateUser',
  SetNewUserRole = '[ManageUsers] SetNewUserRole',
  UpdateUser = '[ManageUsers] UpdateUser',
  LoadUser = '[ManageUsers] LoadUser',
  UserLoaded = '[ManageUsers] UserLoaded',
  SetEditableUserRole = '[ManageUsers] SetEditableUserRole',
  UpdateStatus = '[ManageUsers] UpdateStatus',
  SetStatus = '[ManageUsers] SetStatus',
  SendInvitation = '[ManageUsers] SendInvitation',
  CancelInvitation = '[ManageUsers] CancelInvitation',
  FilterByStatus = '[ManageUsers] FilterByStatus',
  UserCreated = '[ManageUsers] UserCreated',
  ClearSearch = '[ManageUsers] ClearSearch',
  UploadNewUserPhoto = '[ManageUsers] UploadNewUserPhoto',
  SetNewUserPhoto = '[ManageUsers] SetNewUserPhoto',
  ChangePhoto = '[ManageUsers] ChangePhoto',
  PhotoChanged = '[ManageUsers] PhotoChanged',
  CreateDraftUser = '[ManageUsers] CreateDraftUser',
  DraftUserCreated = '[ManageUsers] DraftUserCreated',
  NewUser = '[ManageUsers] NewUser',
  SelectRole = '[ManageUsers] SelectRole',
  ToggleRoleWizard = '[ManageUsers] ToggleRoleWizard',
  AddNewRole = '[ManageUsers] AddNewRole',
  RoleAdded = '[ManageUsers] RoleAdded',
  ExitUser = '[ManageUsers] ExitUser',
  ShowOverview = '[ManageUsers] ShowOverview',
  CreateRole = '[ManageUsers] CreateRole',
  RoleCreated = '[ManageUsers] RoleCreated',
  RemoveRole = '[ManageUsers] RemoveRole',
  AddEmailInput = '[ManageUsers] AddEmailInput',
  RemoveEmailInput = '[ManageUsers] RemoveEmailInput',
  AddPhoneInput = '[ManageUsers] AddPhoneInput',
  RemovePhoneInput = '[ManageUsers] RemovePhoneInput',
  SaveRoles = '[ManageUsers] SaveRoles',
  AssignProject = '[ManageUsers] AssignProject',
  ProjectAssigned = '[ManageUsers] ProjectAssigned',
  UnassignProject = '[ManageUsers] UnassignProject',
  ProjectUnassigned = '[ManageUsers] ProjectUnassigned',
  MainEmailChange = '[ManageUsers] MainEmailChange',
  Empty = '[ManageUsers] Empty',
  GenerateTankCode = '[ManageUsers] GenerateTankCode',
  FailedGenerateTankCode = '[ManageUsers] FailedGenerateTankCode',
  SetDefaults = '[ManageUsers] SetDefaults',
  MainPhoneChange = '[ManageUsers] MainPhoneChange',
}

@HideLoader(
  UsersActionTypes.LoadAll,
  UsersActionTypes.Search,
  UsersActionTypes.ChangePageLimit,
  UsersActionTypes.LoadPage,
  UsersActionTypes.SortBy,
  UsersActionTypes.FilterByStatus,
  UsersActionTypes.UpdateStatus,
)
export class LoadAllSuccess implements Action {
  readonly type = UsersActionTypes.LoadAllSuccess;

  constructor(public payload: APIResponse<User[]>) {}
}

@ShowLoader()
export class LoadAll implements Action {
  readonly type = UsersActionTypes.LoadAll;

  constructor(public payload: number = defaultItemsPerPage) {}
}

@HideLoader(UsersActionTypes.LoadAll)
export class LoadFail implements Action {
  readonly type = UsersActionTypes.LoadFail;

  constructor(readonly err: HttpErrorResponse) {}
}

@ShowLoader()
export class LoadPage implements Action {
  readonly type = UsersActionTypes.LoadPage;

  constructor(public payload: number) {}
}

@ShowLoader()
export class Search implements Action {
  readonly type = UsersActionTypes.Search;

  constructor(public payload: string) {}
}

@ShowLoader()
export class SortBy implements Action {
  readonly type = UsersActionTypes.SortBy;

  constructor(public payload: UsersSortTypes) {}
}

export class DeleteUser implements Action {
  readonly type = UsersActionTypes.DeleteUser;

  constructor(public payload: User) {}
}

export class RestoreUser implements Action {
  readonly type = UsersActionTypes.RestoreUser;

  constructor(public payload: User) {}
}

export class UserDeleted implements Action {
  readonly type = UsersActionTypes.UserDeleted;

  constructor(public payload: APIResponse<User[]>) {}
}

export class UserRestored implements Action {
  readonly type = UsersActionTypes.UserRestored;

  constructor(public payload: APIResponse<User[]>) {}
}

export class LoadRoles implements Action {
  readonly type = UsersActionTypes.LoadRoles;

  constructor() {}
}

export class RolesLoaded implements Action {
  readonly type = UsersActionTypes.RolesLoaded;

  constructor(public payload: Role[]) {}
}

@ShowLoader()
export class ChangePageLimit implements Action {
  readonly type = UsersActionTypes.ChangePageLimit;

  constructor(public payload: number) {}
}

export class CreateUser implements Action {
  readonly type = UsersActionTypes.CreateUser;

  constructor(public payload: UserStatuses = UserStatuses.DRAFT) {}
}

export class SetNewUserRole implements Action {
  readonly type = UsersActionTypes.SetNewUserRole;

  constructor(public payload: Role) {}
}

export class UpdateUser implements Action {
  readonly type = UsersActionTypes.UpdateUser;

  constructor() {}
}

@ShowLoader()
export class LoadUser implements Action {
  readonly type = UsersActionTypes.LoadUser;

  constructor(public payload: UUID) {}
}

export class GenerateTankCode implements Action {
  readonly type = UsersActionTypes.GenerateTankCode;

  constructor(public payload: UUID) {}
}

@HideLoader(UsersActionTypes.LoadAll)
export class FailedGenerateTankCode implements Action {
  readonly type = UsersActionTypes.FailedGenerateTankCode;

  constructor(readonly err: HttpErrorResponse) {}
}

@HideLoader(UsersActionTypes.LoadUser)
export class UserLoaded implements Action {
  readonly type = UsersActionTypes.UserLoaded;

  constructor(public payload: User) {}
}

export class SetEditableUserRole implements Action {
  readonly type = UsersActionTypes.SetEditableUserRole;

  constructor(public payload: Role) {}
}

@ShowLoader()
export class UpdateStatus implements Action {
  readonly type = UsersActionTypes.UpdateStatus;

  constructor(public payload: { user: User; status: string }) {}
}

export class SetStatus implements Action {
  readonly type = UsersActionTypes.SetStatus;

  constructor(public payload: { user: User; totalItems: number }) {}
}

export class SendInvitation implements Action {
  readonly type = UsersActionTypes.SendInvitation;

  constructor(public payload: User) {}
}

export class CancelInvitation implements Action {
  readonly type = UsersActionTypes.CancelInvitation;

  constructor() {}
}

@ShowLoader()
export class FilterByStatus implements Action {
  readonly type = UsersActionTypes.FilterByStatus;

  constructor(public payload: UserStatuses) {}
}

export class UserCreated implements Action {
  readonly type = UsersActionTypes.UserCreated;

  constructor() {}
}

export class ClearSearch implements Action {
  readonly type = UsersActionTypes.ClearSearch;

  constructor() {}
}

export class UploadNewUserPhoto implements Action {
  readonly type = UsersActionTypes.UploadNewUserPhoto;

  constructor(public payload: File) {}
}

export class SetNewUserPhoto implements Action {
  readonly type = UsersActionTypes.SetNewUserPhoto;

  constructor(public payload: string) {}
}

export class ChangePhoto implements Action {
  readonly type = UsersActionTypes.ChangePhoto;

  constructor(public payload: File) {}
}

export class PhotoChanged implements Action {
  readonly type = UsersActionTypes.PhotoChanged;

  constructor(public payload: string) {}
}

export class CreateDraftUser implements Action {
  readonly type = UsersActionTypes.CreateDraftUser;

  constructor(public payload: string) {}
}

export class DraftUserCreated implements Action {
  readonly type = UsersActionTypes.DraftUserCreated;

  constructor(public payload: APIResponse<User>) {}
}

export class NewUser implements Action {
  readonly type = UsersActionTypes.NewUser;

  constructor() {}
}

export class SelectRole implements Action {
  readonly type = UsersActionTypes.SelectRole;

  constructor(public payload: UserRoleUnit) {}
}

export class ToggleRoleWizard implements Action {
  readonly type = UsersActionTypes.ToggleRoleWizard;

  constructor() {}
}

export class AddNewRole implements Action {
  readonly type = UsersActionTypes.AddNewRole;

  constructor(public payload: UserRoleUnit) {}
}

export class ExitUser implements Action {
  readonly type = UsersActionTypes.ExitUser;

  constructor(public payload: UserStatuses) {}
}

export class ShowOverview implements Action {
  readonly type = UsersActionTypes.ShowOverview;

  constructor(public payload: boolean = true) {}
}

export class CreateRole implements Action {
  readonly type = UsersActionTypes.CreateRole;

  constructor(public payload: string) {}
}

export class RoleCreated implements Action {
  readonly type = UsersActionTypes.RoleCreated;

  constructor(public payload: UserRole) {}
}

export class RemoveRole implements Action {
  readonly type = UsersActionTypes.RemoveRole;

  constructor(public payload: UUID) {}
}

export class AddEmailInput implements Action {
  readonly type = UsersActionTypes.AddEmailInput;

  constructor() {}
}

export class RemoveEmailInput implements Action {
  readonly type = UsersActionTypes.RemoveEmailInput;

  constructor(public payload: number) {}
}

export class AddPhoneInput implements Action {
  readonly type = UsersActionTypes.AddPhoneInput;

  constructor() {}
}

export class RemovePhoneInput implements Action {
  readonly type = UsersActionTypes.RemovePhoneInput;

  constructor(public payload: number) {}
}

export class RoleAdded implements Action {
  readonly type = UsersActionTypes.RoleAdded;

  constructor(public payload: number) {}
}

export class SaveRoles implements Action {
  readonly type = UsersActionTypes.SaveRoles;

  constructor() {}
}

export class AssignProject implements Action {
  readonly type = UsersActionTypes.AssignProject;
}

export class ProjectAssigned implements Action {
  readonly type = UsersActionTypes.ProjectAssigned;

  constructor(public payload: ResponseTeamMember) {}
}

export class UnassignProject implements Action {
  readonly type = UsersActionTypes.UnassignProject;

  constructor(public payload: UserProject) {}
}

export class ProjectUnassigned implements Action {
  readonly type = UsersActionTypes.ProjectUnassigned;

  constructor(public payload: UserProject) {}
}

export class MainEmailChange implements Action {
  readonly type = UsersActionTypes.MainEmailChange;

  constructor(public payload: FormControlState<string>) {}
}

export class MainPhoneChange implements Action {
  readonly type = UsersActionTypes.MainPhoneChange;

  constructor() {}
}

export class SetDefaults implements Action {
  readonly type = UsersActionTypes.SetDefaults;

  constructor(public language: string | null, public timezone: string) {}
}

export class Empty implements Action {
  readonly type = UsersActionTypes.Empty;

  constructor() {}
}

export type UsersActions =
  | LoadAllSuccess
  | LoadAll
  | LoadFail
  | LoadPage
  | Search
  | SortBy
  | DeleteUser
  | RestoreUser
  | UserDeleted
  | UserRestored
  | LoadRoles
  | RolesLoaded
  | ChangePageLimit
  | CreateUser
  | SetNewUserRole
  | UpdateUser
  | LoadUser
  | UserLoaded
  | SetEditableUserRole
  | UpdateStatus
  | SetStatus
  | SendInvitation
  | CancelInvitation
  | FilterByStatus
  | UserCreated
  | ClearSearch
  | UploadNewUserPhoto
  | SetNewUserPhoto
  | ChangePhoto
  | PhotoChanged
  | CreateDraftUser
  | DraftUserCreated
  | NewUser
  | SelectRole
  | ToggleRoleWizard
  | AddNewRole
  | ExitUser
  | ShowOverview
  | CreateRole
  | RoleCreated
  | RemoveRole
  | AddEmailInput
  | RemoveEmailInput
  | AddPhoneInput
  | RemovePhoneInput
  | SaveRoles
  | RoleAdded
  | AssignProject
  | ProjectAssigned
  | UnassignProject
  | ProjectUnassigned
  | SetDefaults
  | MainEmailChange
  | MainPhoneChange
  | Empty;
